// Shared types for the AGP resume reformatting tool.
//
// The structured resume model follows the client spec (§8): dates carry
// precision (a year-only date stays year-only in the output), experience is
// employer-grouped (one employer, several roles beneath it — the "HSBC"
// case from the spec's Appendix B), and nothing the parser could not map is
// dropped — it lands in unmappedContent for the reviewer to place or discard.

// ---- users -------------------------------------------------------------------

export type UserRole = "user" | "admin";

export interface ApiErrorBody {
  error: string;
  message: string;
  details?: unknown;
}

// ---- structured resume model -------------------------------------------------

/** A date as it appeared in the resume. month=null means year-only precision;
 *  the renderer must NOT invent a month for it. isPresent covers "Present",
 *  "Current", "to date" and equivalents. */
export interface ExtractedDate {
  rawText: string | null;
  year: number | null;
  month: number | null;
  isPresent: boolean;
  confidence: number;
}

/** The marker a list line is drawn with (AGP feedback: CVs use arrows,
 *  numbers, dashes… and nest up to five deep). */
export type BulletMarker = "dot" | "circle" | "square" | "arrow" | "dash" | "number" | "letter";

export const BULLET_MARKERS: Array<{ key: BulletMarker; label: string; glyph: string }> = [
  { key: "dot", label: "Dot", glyph: "•" },
  { key: "circle", label: "Circle", glyph: "◦" },
  { key: "square", label: "Square", glyph: "▪" },
  { key: "arrow", label: "Arrow", glyph: "➢" },
  { key: "dash", label: "Dash", glyph: "–" },
  { key: "number", label: "Number", glyph: "1." },
  { key: "letter", label: "Letter", glyph: "a." },
];

export const MAX_BULLET_LEVEL = 4;

/** One line of content under a role or other-section (AGP Aug 2026 feedback:
 *  not everything from a resume is a bullet). */
export interface ContentLine {
  text: string;
  /** "para" renders as plain text with no marker (narrative prose, 自己PR,
   *  成果 paragraphs); "bullet" is a list line at `level` with `marker`.
   *  "sub" and "sub2" are how older revisions wrote levels 1 and 2 and are
   *  read as such. */
  kind: "para" | "bullet" | "sub" | "sub2";
  /** Nesting depth 0–4 of a list line. */
  level?: number;
  /** Marker style of a list line; unset means the level's usual one. */
  marker?: BulletMarker;
}

export const isListLine = (l: ContentLine) => l.kind !== "para";

export function lineLevel(l: ContentLine): number {
  const base = l.kind === "sub" ? 1 : l.kind === "sub2" ? 2 : (l.level ?? 0);
  return Math.max(0, Math.min(MAX_BULLET_LEVEL, Math.floor(base)));
}

const DEFAULT_MARKERS: BulletMarker[] = ["dot", "circle", "square", "dash", "dot"];

export function lineMarker(l: ContentLine): BulletMarker {
  return l.marker ?? DEFAULT_MARKERS[lineLevel(l)]!;
}

/** What a list line's marker looks like; `n` numbers it within its list. */
export function markerGlyph(marker: BulletMarker, n: number): string {
  if (marker === "number") return `${n}.`;
  if (marker === "letter") return `${String.fromCharCode(96 + ((n - 1) % 26) + 1)}.`;
  return BULLET_MARKERS.find((m) => m.key === marker)!.glyph;
}

/** A line in today's shape: legacy sub/sub2 become explicit levels. */
export function normalizeLine(l: ContentLine): ContentLine {
  if (l.kind === "para") return { text: l.text, kind: "para" };
  return { text: l.text, kind: "bullet", level: lineLevel(l), marker: lineMarker(l) };
}

/** Which of the template's sections print, in what order, for one resume:
 *  the reviewer's order first (only keys the template has), then anything
 *  the template adds in its own order. */
export function orderedSections<T extends { key: SectionKey }>(sections: T[], order: SectionKey[] | null | undefined): T[] {
  if (!order || order.length === 0) return sections;
  const byKey = new Map(sections.map((s) => [s.key, s]));
  const out: T[] = [];
  for (const key of order) {
    const s = byKey.get(key);
    if (s && !out.includes(s)) out.push(s);
  }
  for (const s of sections) if (!out.includes(s)) out.push(s);
  return out;
}

/** One job title held at an employer. Employers with a single role still use
 *  this shape with roles.length === 1. */
export interface RoleEntry {
  title: string | null;
  /** The role's own place when one employer spans several ("Tacoma, London,
   *  Tokyo and Seattle" at the employer, "Tokyo" on this title). Optional
   *  for revisions saved before the field existed. */
  location?: string | null;
  start: ExtractedDate | null;
  end: ExtractedDate | null;
  /** Kept as "bullets" for stored-revision compatibility; readers must
   *  tolerate plain strings from old revisions (treated as kind "bullet"). */
  bullets: ContentLine[];
  confidence: number;
}

export interface EmploymentEntry {
  employer: string | null;
  employerLocation: string | null;
  /** Facts about the employer itself (上場区分, 従業員数, 事業内容, industry
   *  blurbs) — rendered as a plain paragraph under the employer line, never
   *  as bullets (AGP feedback). */
  employerDescription: string | null;
  /** 規模 / scale / team-size column value for this engagement, exactly as
   *  written ("70名（パートナー会社含む）"). Its own field because the value
   *  kept getting lost inside role content (AGP feedback); rendered as its
   *  own 規模 line under the employer description. */
  scale: string | null;
  /** Employer-level span (shown once, roles listed beneath). */
  start: ExtractedDate | null;
  end: ExtractedDate | null;
  roles: RoleEntry[];
  confidence: number;
}

export interface EducationEntry {
  institution: string | null;
  location: string | null;
  qualification: string | null;
  field: string | null;
  startYear: number | null;
  endYear: number | null;
  /** 1–12, only when the source states a month ("Sept. 2004 – May 2006").
   *  Optional for stored revisions from before month support; readers treat
   *  absence as year-only. */
  startMonth?: number | null;
  endMonth?: number | null;
  honours: string | null;
  bullets: string[];
  confidence: number;
}

export interface LanguageEntry {
  language: string;
  /** Proficiency exactly as stated in the resume ("fluent", "JLPT N2"). */
  statedLevel: string | null;
  confidence: number;
}

export interface CertificationEntry {
  name: string | null;
  issuer: string | null;
  year: number | null;
  confidence: number;
}

/** Source content that didn't fit a standard section (patents, awards,
 *  memberships, interests...). Rendered under its own heading. */
export interface OtherSection {
  heading: string;
  /** Same shape and compatibility rule as RoleEntry.bullets. */
  bullets: ContentLine[];
  confidence: number;
}

export interface CandidateDetails {
  fullName: string | null;
  /** Current location as displayed ("Tokyo, Japan"). */
  location: string | null;
  /** Only when explicitly present in the resume. */
  nationality: string | null;
  /** Contact details are extracted but shown in output only when the
   *  generation request asks for them (client-facing copies omit them). */
  email: string | null;
  phone: string | null;
  /** AGP-internal candidate reference — typed by the reviewer, never
   *  extracted from the resume (resumes don't carry AGP IDs). */
  referenceId: string | null;
  /** The four below are extracted only when the resume states them
   *  explicitly, and kept as free text exactly as written — never parsed,
   *  reformatted or invented (client spec §6.2 anti-fabrication). */
  citizenship: string | null;
  dateOfBirth: string | null;
  annualSalary: string | null;
  availability: string | null;
  languages: LanguageEntry[];
  /** Professional summary lines: paragraphs, bullets and sub-bullets (AGP
   *  feedback: summaries aren't always bulleted). Same compatibility rule as
   *  RoleEntry.bullets — plain strings from old revisions become bullets. */
  summaryBullets: ContentLine[];
  confidence: number;
}

/** The Consultant Summary block (AGP Aug 2026 feedback): who at AGP prepared
 *  the report. Filled on the review screen — picked from the app-wide
 *  consultant list (Settings page) or typed by hand — never extracted from
 *  the resume. Fields left null render as blank lines the consultant
 *  completes in Word. */
export interface ConsultantInfo {
  preparedBy: string | null;
  telephone: string | null;
  email: string | null;
  date: string | null;
}

/** One entry in the app-wide consultant list (Settings → Consultants).
 *  Offered as a dropdown on the review screen; picking one fills the
 *  Consultant Summary's Prepared by / Telephone / Email. */
export interface Consultant {
  name: string;
  telephone: string;
  email: string;
}

/** One named footer text (Settings → Footer texts). A template names its
 *  default preset; the review screen can swap in another per resume. */
export interface FooterPreset {
  name: string;
  lines: string[];
}

/** The footer lines a document renders: the reviewer's pick when it names a
 *  preset that still exists, else the template's default preset, else the
 *  template's own footer lines. */
export function resolveFooterLines(
  tpl: TemplateDefinition,
  presets: FooterPreset[],
  chosen?: string | null,
): string[] {
  const find = (name: string | null | undefined) =>
    name ? presets.find((p) => p.name === name) : undefined;
  const preset = find(chosen) ?? find(tpl.branding.footerPreset);
  return preset ? preset.lines : tpl.branding.footerLines;
}

/** The template with its footer lines swapped, so renderers and the preview
 *  keep reading tpl.branding.footerLines. */
export function withFooterLines(tpl: TemplateDefinition, lines: string[]): TemplateDefinition {
  return lines === tpl.branding.footerLines
    ? tpl
    : { ...tpl, branding: { ...tpl.branding, footerLines: lines } };
}

/** The role the candidate is being put forward for — the "Client:" /
 *  "Position:" lines that open an AGP Candidate Report. Typed on the review
 *  screen, or picked up when the source is itself an AGP report. Null
 *  fields render as blank lines the consultant completes in Word. */
export interface CandidateReportInfo {
  client: string | null;
  position: string | null;
}

export interface AgpResume {
  candidate: CandidateDetails;
  /** Optional so revisions saved before the field existed stay readable. */
  consultant?: ConsultantInfo | null;
  /** Optional for the same reason. */
  candidateReport?: CandidateReportInfo | null;
  experience: EmploymentEntry[];
  education: EducationEntry[];
  /** Business/domain competencies (asset classes, markets, methodologies...). */
  coreCompetencies: string[];
  /** Tools, platforms, technical skills. */
  technicalSkills: string[];
  certifications: CertificationEntry[];
  publications: string[];
  careerInterests: string[];
  otherSections: OtherSection[];
  /** Source text the parser could not place. Never silently dropped
   *  (spec FR-025); shown to the reviewer, never rendered into output. */
  unmappedContent: string[];
  extractionWarnings: string[];
  /** The reviewer's section order for this resume (AGP feedback): sorts
   *  the template's sections for this document only. Absent means the
   *  template's own order. */
  sectionOrder?: SectionKey[];
}

// ---- job descriptions --------------------------------------------------------
// Second document type beside resumes: a client job posting (pasted text,
// uploaded file or fetched URL) extracted into this model and rendered into
// an AGP-branded JD document (the "AGP - <title> - <company>" format).

export interface JdDetailRow {
  label: string;
  value: string;
}

/** One block of the posting under its own heading (Position Overview,
 *  Responsibilities, Requirements, Benefits…). Headings stay as the source
 *  wrote them; the reviewer can rename them. */
export interface JdSection {
  heading: string;
  lines: ContentLine[];
}

/** The "Please Contact" block — the AGP consultant handling the role.
 *  Set on the template (fixed) or per job on the review screen, never
 *  extracted from the posting. Phones/emails/websites are lists — a contact
 *  can carry several of each; readers must tolerate plain strings from
 *  revisions stored before the fields became lists. Empty fields are
 *  skipped in the output. */
export interface JdContact {
  name: string | null;
  title: string | null;
  company: string | null;
  telephone: string[];
  mobile: string[];
  email: string[];
  website: string[];
}

/** List coercion for JdContact fields: tolerates the pre-list single-string
 *  shape and drops blanks. */
export function jdContactList(v: string[] | string | null | undefined): string[] {
  const list = typeof v === "string" ? [v] : Array.isArray(v) ? v : [];
  return list.map((s) => (typeof s === "string" ? s.trim() : "")).filter(Boolean);
}

export function jdContactHasContent(c: JdContact | null | undefined): boolean {
  if (!c) return false;
  return !!(
    c.name ||
    c.title ||
    c.company ||
    jdContactList(c.telephone).length ||
    jdContactList(c.mobile).length ||
    jdContactList(c.email).length ||
    jdContactList(c.website).length
  );
}

export interface AgpJobDescription {
  /** Position title ("Investor Relations Director"). */
  title: string | null;
  company: string | null;
  location: string | null;
  /** Extra labeled facts beyond company/location (salary, employment type,
   *  business area…), exactly as stated in the source. */
  details: JdDetailRow[];
  /** The posting's content, order preserved. */
  sections: JdSection[];
  contact: JdContact | null;
  /** Source text the parser could not place — shown to the reviewer, never
   *  rendered into output (same rule as resumes). */
  unmappedContent: string[];
  extractionWarnings: string[];
}

/** The label rows under the JD title (Company / Location + extra details).
 *  Shared by the DOCX renderer and the HTML preview. */
export function jdHeaderRows(jd: AgpJobDescription): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  if (jd.company) rows.push(["Company:", jd.company]);
  if (jd.location) rows.push(["Location:", jd.location]);
  for (const d of jd.details) {
    if (d.label.trim() && d.value.trim()) {
      rows.push([`${d.label.replace(/[:：]\s*$/, "").trim()}:`, d.value.trim()]);
    }
  }
  return rows;
}

/** The contact block a JD document renders: the job's own contact when the
 *  reviewer filled any field, else the template's fixed contact (set in the
 *  builder — the standard case, so every JD from a template names the same
 *  consultant). */
export function resolveJdContact(
  jd: AgpJobDescription,
  tpl: TemplateDefinition,
): JdContact | null {
  if (jdContactHasContent(jd.contact)) return jd.contact;
  return jdContactHasContent(tpl.jdContact) ? (tpl.jdContact ?? null) : null;
}

/** Labeled contact lines (name, title and company are rendered separately). One row
 *  per value — every phone, email and website gets its own line. */
export function jdContactRows(contact: JdContact | null): Array<[string, string]> {
  if (!contact) return [];
  const rows: Array<[string, string]> = [];
  for (const v of jdContactList(contact.telephone)) rows.push(["Main:", v]);
  for (const v of jdContactList(contact.mobile)) rows.push(["Mobile:", v]);
  for (const v of jdContactList(contact.email)) rows.push(["Email:", v]);
  for (const v of jdContactList(contact.website)) rows.push(["", v]);
  return rows;
}

// ---- resume jobs -------------------------------------------------------------

export type ResumeJobStatus =
  | "created"
  | "extracting"
  | "ready_for_review"
  | "failed";

export interface ResumeJobSummary {
  id: string;
  status: ResumeJobStatus;
  sourceFilename: string;
  /** Files uploaded for this job; more than one when a candidate arrived
   *  as an English CV plus Japanese 履歴書/職務経歴書. */
  sourceCount: number;
  candidateName: string | null;
  templateId: string | null;
  error: string | null;
  outputCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ResumeJobDetail extends ResumeJobSummary {
  warnings: string[];
  pageCount: number | null;
  extractionMethod: string | null;
  model: string | null;
}

/** One uploaded file of a job. Files flagged appendToOutput are attached
 *  verbatim after the generated report, the way the consumer team pastes
 *  the Japanese originals under the English report by hand. */
export interface ResumeSourceInfo {
  id: string;
  filename: string;
  mimeType: string;
  url: string;
  appendToOutput: boolean;
  /** False when the server lacks the tool to attach this file type
   *  (LibreOffice for Word/Excel, poppler for PDF). */
  canAppend: boolean;
  redactions: SourceRedactions;
  textEdits: SourceTextEdits;
  /** Word-family files can be edited as text; PDFs only as pages. */
  editableText: boolean;
}

/** A white box drawn over part of an appended page, as fractions of the
 *  page's width and height so it survives any render size. With `text`
 *  the box also prints replacement text (a corrected address, a note).
 *  With `cut` the page band the box spans is taken out across the full
 *  width and what follows moves up, so removed table rows leave no gap. */
export interface RedactionBox {
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  text?: string;
  cut?: boolean;
}

/** The bands cut from one page, merged and in order, as fractions of
 *  its height; shared by the preview and the output render. */
export function cutBands(boxes: RedactionBox[], page: number): Array<{ y: number; h: number }> {
  const bands = boxes
    .filter((b) => b.page === page && b.cut && b.h > 0)
    .map((b) => ({ y: b.y, h: b.h }))
    .sort((a, b) => a.y - b.y);
  const out: Array<{ y: number; h: number }> = [];
  for (const band of bands) {
    const last = out[out.length - 1];
    if (last && band.y <= last.y + last.h) last.h = Math.max(last.h, band.y + band.h - last.y);
    else out.push({ ...band });
  }
  return out;
}

/** How text is laid out inside a box of the given size (any unit): the
 *  font size that fits every line, and the lines. Shared by the preview
 *  (CSS) and the output (SVG) so both draw the same thing. */
export function boxTextLayout(text: string, width: number, height: number): { fontPx: number; lineHeight: number; lines: string[]; padX: number; padY: number } {
  const lines = text.replace(/\r/g, "").split("\n");
  const charWidth = (ch: string) => (/[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\uFF01-\uFF60]/u.test(ch) ? 1 : 0.58);
  const widest = Math.max(1, ...lines.map((l) => [...l].reduce((s, ch) => s + charWidth(ch), 0)));
  const padX = width * 0.03;
  const padY = height * 0.08;
  const fontPx = Math.max(4, Math.min((height - 2 * padY) / (lines.length * 1.25), (width - 2 * padX) / widest));
  return { fontPx, lineHeight: fontPx * 1.25, lines, padX, padY };
}

/** A table row the reviewer added: inserted after row `after` of the
 *  document's `table`-th table (both counted in the original, -1 for the
 *  top), one text per cell, formatted like the row it follows. */
export interface SourceAddedRow {
  table: number;
  after: number;
  cells: string[];
}

/** Text changes to an appended Word original: paragraph number (in
 *  document order, table cells included) → new text, paragraphs removed
 *  outright, and table rows removed or added (tables and rows numbered
 *  in the original). Applied to the file itself at generate time so its
 *  formatting is kept. */
export interface SourceTextEdits {
  texts: Record<string, string>;
  removed: number[];
  removedRows?: Array<{ table: number; row: number }>;
  addedRows?: SourceAddedRow[];
}

export const EMPTY_TEXT_EDITS: SourceTextEdits = { texts: {}, removed: [], removedRows: [], addedRows: [] };

export function normalizeTextEdits(raw: unknown): SourceTextEdits {
  const r = (raw ?? {}) as Partial<Record<keyof SourceTextEdits, unknown>>;
  const texts: Record<string, string> = {};
  if (r.texts && typeof r.texts === "object") {
    for (const [k, v] of Object.entries(r.texts as Record<string, unknown>)) {
      if (/^\d+$/.test(k) && typeof v === "string") texts[k] = v.slice(0, 5000);
    }
  }
  const removed = Array.isArray(r.removed)
    ? [...new Set(r.removed.filter((p): p is number => Number.isInteger(p) && (p as number) >= 0))].sort((a, b) => a - b)
    : [];
  const index = (v: unknown, min: number) => (Number.isInteger(v) && (v as number) >= min ? (v as number) : null);
  const removedRows = Array.isArray(r.removedRows)
    ? r.removedRows.flatMap((x) => {
        const o = (x ?? {}) as Record<string, unknown>;
        const table = index(o.table, 0);
        const row = index(o.row, 0);
        return table != null && row != null ? [{ table, row }] : [];
      })
    : [];
  const addedRows = Array.isArray(r.addedRows)
    ? r.addedRows.flatMap((x) => {
        const o = (x ?? {}) as Record<string, unknown>;
        const table = index(o.table, 0);
        const after = index(o.after, -1);
        if (table == null || after == null || !Array.isArray(o.cells)) return [];
        return [{ table, after, cells: o.cells.map((c) => (typeof c === "string" ? c.slice(0, 5000) : "")) }];
      })
    : [];
  return { texts, removed, removedRows, addedRows };
}

export const hasTextEdits = (e: SourceTextEdits) =>
  Object.keys(e.texts).length > 0 || e.removed.length > 0 || (e.removedRows?.length ?? 0) > 0 || (e.addedRows?.length ?? 0) > 0;

/** A paragraph's resolved look in a Word original, so the text view shows
 *  it the way the file prints. Sizes in points, indents in points. */
export interface SourceParaStyle {
  font?: string;
  fontPt?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  /** Hex RGB without '#'. */
  color?: string;
  align?: "left" | "center" | "right" | "justify";
  indentLeft?: number;
  /** Negative for a hanging indent. */
  indentFirst?: number;
  spaceBefore?: number;
  spaceAfter?: number;
  /** Multiple of single spacing. */
  lineHeight?: number;
}

export interface SourceTextPara {
  id: number;
  text: string;
  style?: SourceParaStyle;
  /** List marker ("•", "1.", "➢") when the paragraph is numbered. */
  bullet?: string;
}

export interface SourceTextCell {
  span: number;
  /** Rows the cell covers when merged downwards (1 when absent). */
  rowSpan?: number;
  /** The cell's own borders when it sets them; else the table's. */
  borders?: { top: boolean; right: boolean; bottom: boolean; left: boolean };
  vAlign?: "top" | "center" | "bottom";
  paras: SourceTextPara[];
}

/** The editable structure of a Word original, for the preview's text view. */
export type SourceTextBlock =
  | ({ kind: "para" } & SourceTextPara)
  | {
      kind: "table";
      bordered: boolean;
      widths: number[];
      /** Minimum height of each row in points, 0 when the row sizes itself. */
      rowHeights?: number[];
      rows: SourceTextCell[][];
    };

export interface SourceTextDoc {
  /** Document defaults (font and size) every paragraph starts from. */
  defaults: SourceParaStyle;
  blocks: SourceTextBlock[];
}

/** What the reviewer changed on an appended original: pages dropped from
 *  the output and boxes painted white (a 履歴書's address, phone and
 *  e-mail cells). Zero-based page indexes. */
export interface SourceRedactions {
  removedPages: number[];
  boxes: RedactionBox[];
}

export const EMPTY_REDACTIONS: SourceRedactions = { removedPages: [], boxes: [] };

/** One rendered page of an appended original, for the preview. */
export interface SourcePageInfo {
  index: number;
  url: string;
  width: number;
  height: number;
  /** The page's text rows as bands of its height (fractions), each from
   *  the bottom of the row above to its own bottom. Only for pages
   *  rendered from a PDF's own text. */
  lines?: Array<{ y: number; h: number }>;
}

/** Bounds-checked copy of untrusted redaction JSON (API input, stored
 *  rows); anything malformed is dropped rather than rejected. */
export function normalizeRedactions(raw: unknown): SourceRedactions {
  const r = (raw ?? {}) as Partial<Record<keyof SourceRedactions, unknown>>;
  const pageNo = (v: unknown) => (Number.isInteger(v) && (v as number) >= 0 ? (v as number) : null);
  const frac = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : null);
  const removedPages = Array.isArray(r.removedPages)
    ? [...new Set(r.removedPages.map(pageNo).filter((p): p is number => p != null))].sort((a, b) => a - b)
    : [];
  const boxes = Array.isArray(r.boxes)
    ? r.boxes.flatMap((b) => {
        const o = (b ?? {}) as Record<string, unknown>;
        const page = pageNo(o.page);
        const x = frac(o.x);
        const y = frac(o.y);
        const w = frac(o.w);
        const h = frac(o.h);
        if (page == null || x == null || y == null || w == null || h == null) return [];
        if (w <= 0 || h <= 0) return [];
        const text = typeof o.text === "string" ? o.text.slice(0, 500) : "";
        return [{ page, x, y, w: Math.min(w, 1 - x), h: Math.min(h, 1 - y), ...(text.trim() ? { text } : {}), ...(o.cut === true ? { cut: true } : {}) }];
      })
    : [];
  return { removedPages, boxes };
}

export interface ParsedPayload {
  /** The AI extraction, untouched (revisions never overwrite it — spec §8). */
  extracted: AgpResume | null;
  /** Latest reviewer-edited version; null until the first edit. */
  edited: AgpResume | null;
  warnings: string[];
  /** Spelling/grammar corrections applied to the text, newest run. */
  grammarFixes?: GrammarFix[];
}

/** One line the spelling-and-grammar pass changed: only spelling, grammar
 *  and first-person pronouns, never a paraphrase. `path` is the dotted
 *  location in the resume (e.g. "experience.0.roles.1.bullets.3.text"). */
export interface GrammarFix {
  path: string;
  before: string;
  after: string;
}

/** The label the output prints before an entry's scale/team-size value:
 *  規模 for a Japanese value, "Team" for an English one. */
export function scaleLabel(scale: string): string {
  return /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u.test(scale) ? "規模" : "Team";
}

export interface OutputInfo {
  id: string;
  format: "docx" | "pdf";
  filename: string;
  templateId: string;
  templateVersion: number;
  sizeBytes: number;
  downloadUrl: string;
  createdAt: string;
}

export interface UploadInstructions {
  jobId: string;
  uploadUrl: string;
  s3Key: string;
  maxBytes: number;
  contentType: string;
}

// ---- templates ---------------------------------------------------------------

export type SectionKey =
  | "candidateReport"
  | "personalDetails"
  | "consultantSummary"
  | "summary"
  | "education"
  | "experience"
  | "coreCompetencies"
  | "technicalSkills"
  | "certifications"
  | "publications"
  | "careerInterests"
  | "otherSections";

/** Rows a template may show under Personal Details. Which rows appear, their
 *  order and their labels are template settings (personalDetails on
 *  TemplateDefinition); rows whose value is empty are skipped in the output. */
export type PersonalDetailKey =
  | "referenceId"
  | "fullName"
  | "location"
  | "nationality"
  | "citizenship"
  | "languages"
  | "dateOfBirth"
  | "annualSalary"
  | "availability"
  | "email"
  | "phone";

export interface PersonalDetailField {
  key: PersonalDetailKey;
  label: string;
}

export const ALL_PERSONAL_DETAIL_KEYS: PersonalDetailKey[] = [
  "referenceId",
  "fullName",
  "location",
  "nationality",
  "citizenship",
  "languages",
  "dateOfBirth",
  "annualSalary",
  "availability",
  "email",
  "phone",
];

export const DEFAULT_PERSONAL_DETAIL_LABELS: Record<PersonalDetailKey, string> = {
  referenceId: "Reference ID",
  fullName: "Name",
  location: "Location",
  nationality: "Nationality",
  citizenship: "Citizenship",
  languages: "Language Skills",
  dateOfBirth: "Date of Birth",
  annualSalary: "Annual Salary",
  availability: "Availability",
  email: "Email",
  phone: "Telephone",
};

/** Default Personal Details rows per the AGP Aug 2026 feedback: reference ID
 *  through availability, NO email/phone. Nationality stays in the list even
 *  though the feedback mock only shows Citizenship — resumes state 国籍 far
 *  more often than citizenship, empty rows are skipped anyway, and dropping
 *  it would silently lose data. Remove it per-template if unwanted. */
export const DEFAULT_PERSONAL_DETAILS: PersonalDetailField[] = (
  [
    "referenceId",
    "location",
    "nationality",
    "citizenship",
    "languages",
    "dateOfBirth",
    "annualSalary",
    "availability",
  ] as PersonalDetailKey[]
).map((key) => ({ key, label: DEFAULT_PERSONAL_DETAIL_LABELS[key] }));

export interface TemplateSection {
  key: SectionKey;
  label: string;
}

export type PaperSize = "A4" | "Letter";

/** Page dimensions in twips (Word's unit; 1440 = 1 inch). Shared by the DOCX
 *  renderer and the builder's HTML preview so both agree on layout. */
export const PAPER_SIZES: Record<PaperSize, { widthTwips: number; heightTwips: number }> = {
  A4: { widthTwips: 11906, heightTwips: 16838 },
  Letter: { widthTwips: 12240, heightTwips: 15840 },
};

/** A resume template, fully data-driven. Adding a template to the tool means
 *  adding one of these (plus optional logo/watermark assets) — the DOCX
 *  renderer consumes the definition; no renderer changes needed.
 *  Published templates are immutable: visual changes bump `version`. */
export interface TemplateDefinition {
  id: string;
  version: number;
  /** What the template renders. Optional for definitions stored before JD
   *  support; readers treat absence as "resume". JD templates use the
   *  branding/fonts/colors/page settings; resume-only settings (sections,
   *  photo, date format…) are ignored by the JD renderer. */
  kind?: "resume" | "jd";
  name: string;
  description: string;
  fonts: {
    /** Word font family names (must exist on machines opening the DOCX). */
    body: string;
    heading: string;
    /** Half-point sizes are handled by the renderer; these are in points. */
    bodyPt: number;
    namePt: number;
    headingPt: number;
    /** Size of the bullet glyph itself (list text stays bodyPt). Optional;
     *  readers default to the body size. */
    bulletPt?: number;
  };
  colors: {
    /** Hex without '#', as Word expects (e.g. "1F3864"). */
    heading: string;
    text: string;
    subtle: string;
  };
  page: {
    /** Optional for definitions stored before paper-size support; readers
     *  must treat a missing value as "A4". */
    size?: PaperSize;
    /** Margins in twips (1440 = 1 inch). */
    marginTop: number;
    marginBottom: number;
    marginLeft: number;
    marginRight: number;
  };
  /** Width of the left date column, in twips. */
  dateColumnTwips: number;
  /** How month+year dates render (see formatDate for each style's output,
   *  e.g. "MMM.'yy" → "Mar.'11", the AGP-preferred style). Year-only dates
   *  render as just the year in every format. There is deliberately no
   *  day-level format: ExtractedDate carries no day, so one would lie. */
  dateFormat: "MMM yyyy" | "MMMM yyyy" | "yyyy.MM" | "MMM.'yy" | "MM/yyyy" | "yyyy年M月";
  /** Candidate-name casing: "upper" → TARO YAMADA (the AGP-preferred style),
   *  "none" → as written in the resume. Optional for older stored
   *  definitions; readers default to "upper". */
  nameCase?: "upper" | "none";
  /** Language line style: "dash" → "French – Intermediate" (the
   *  AGP-preferred style), "paren" → "French (Intermediate)". Optional for
   *  older stored definitions; readers default to "dash". */
  languageFormat?: "dash" | "paren";
  /** Experience-section styling. Optional for older stored definitions;
   *  readers default to non-italic titles with the employer rule shown. */
  experienceStyle?: {
    /** Italicize role titles. AGP feedback asks for regular text. */
    titleItalic: boolean;
    /** Continuous underline across the whole employer row (dates + employer
     *  + location), as in the AGP sample report. */
    employerRule: boolean;
  };
  /** Education-section styling. Optional; readers default to no rule. */
  educationStyle?: {
    /** Continuous underline across each school row (years + institution +
     *  location), like the experience employer rule (AGP Aug 2026 feedback). */
    schoolRule: boolean;
  };
  branding: {
    /** Asset filename resolved against the template's on-disk asset directory
     *  (used by the built-in seed templates); missing assets degrade
     *  gracefully (text-only header, no watermark). */
    logoFile: string | null;
    /** S3 key of a logo uploaded through the template builder. Takes
     *  precedence over logoFile when both exist. */
    logoS3Key?: string | null;
    /** Rendered logo size in pixels (Word EMU conversion is the renderer's job). */
    logoSize: { width: number; height: number };
    /** Header logo alignment (the company-name fallback follows it).
     *  Optional for older stored definitions; readers default to "right". */
    logoAlign?: "left" | "center" | "right";
    /** How the logo occupies header space. "floating" pins it inside the top
     *  margin so resizing it never pushes the candidate name down; "inline"
     *  is the old flow behavior where a tall logo moves the body start lower.
     *  Optional for older stored definitions; readers default to "floating". */
    logoLayout?: "floating" | "inline";
    watermarkFile: string | null;
    watermarkS3Key?: string | null;
    watermarkSize: { width: number; height: number };
    /** Watermark anchor on the page. Optional for older stored definitions;
     *  readers default to center / center. */
    watermarkHAlign?: "left" | "center" | "right";
    watermarkVAlign?: "top" | "center" | "bottom";
    /** Image opacity 0.05–1, baked into the embedded PNG at render time
     *  (Word has no native image-opacity control). Default 1. */
    logoOpacity?: number;
    watermarkOpacity?: number;
    /** Text shown in the header when no logo asset exists yet. */
    companyName: string;
    footerLines: string[];
    /** Name of the default footer preset (Settings → Footer texts). Absent,
     *  or naming a preset since deleted, falls back to footerLines. */
    footerPreset?: string;
    /** Footer text styling. Optional; defaults to 8pt, regular, subtle grey,
     *  with a 1pt rule above the lines (AGP feedback wants the overline on
     *  every template, so absent/omitted showRule means ON). */
    footerStyle?: {
      /** Word font family name. Optional; readers default to the body font. */
      font?: string;
      sizePt: number;
      bold: boolean;
      /** Hex without '#'. */
      color: string;
      /** Horizontal rule above the footer lines. Readers default to true. */
      showRule: boolean;
      /** Rule thickness in points. Optional; readers default to 1. */
      ruleWidthPt?: number;
      /** Gap between the rule and the first footer line, in points.
       *  Optional; readers default to 4. */
      ruleSpacingPt?: number;
    };
  };
  /** Section sub-header styling. Optional; when the whole block is absent,
   *  readers default to uppercase with a grey highlight
   *  (DEFAULT_HEADING_HIGHLIGHT) and the underline rule — the AGP-preferred
   *  look. An explicit `highlight: null` means the author chose no
   *  highlight, so readers must not re-apply the grey default to it. */
  headingStyle?: {
    case: "upper" | "title" | "none";
    /** Background highlight fill (hex without '#'), or null for none. */
    highlight: string | null;
    showRule: boolean;
  };
  /** Extra emphasis on the employer line of CURRENT employment (an entry
   *  whose employer or any role is marked "Present"). Optional; default off. */
  currentEmployment?: {
    underline: boolean;
    useHeadingColor: boolean;
  };
  /** Fixed "Please Contact" block for JD templates — set in the builder so
   *  every JD generated from this template names the same consultant. A
   *  reviewer can still override it per job on the review screen's Contact
   *  tab (see resolveJdContact). Ignored by the resume renderer. */
  jdContact?: JdContact;
  /** The contact block's heading line — its wording and styling. Optional;
   *  readers default to "Please Contact:" in bold body-size text color.
   *  The family stays the body font (only the contact lines take
   *  jdContactStyle's font). */
  jdContactHeading?: {
    text: string;
    sizePt: number;
    bold: boolean;
    /** Hex without '#'. */
    color: string;
  };
  /** Styling for the contact block's lines (the heading above has its own
   *  block, as in the AGP sample where the details are smaller grey text).
   *  Optional; readers default to the body font and size in the template
   *  text color. */
  jdContactStyle?: {
    /** Word font family name. */
    font: string;
    sizePt: number;
    /** Bold every contact line (the name and labels are bold regardless).
     *  Optional for older stored definitions; readers default to false. */
    bold?: boolean;
    /** Hex without '#'. */
    color: string;
  };
  /** Candidate photo placement. The photo itself comes from the resume job
   *  (auto-extracted from Word/PDF sources or uploaded on the review
   *  screen); the template only decides whether/where/how large.
   *  "left"/"right" sit beside the name; the above/below variants are
   *  full-width rows with their own alignment, so the photo can be kept
   *  clear of the floating logo. */
  photo?: {
    show: boolean;
    position:
      | "left"
      | "right"
      | "above-left"
      | "above-center"
      | "above-right"
      | "below-left"
      | "below-center"
      | "below-right";
    widthPx: number;
    heightPx: number;
  };
  /** Rows shown under Personal Details, in order, with display labels.
   *  Optional; readers default to DEFAULT_PERSONAL_DETAILS (the AGP Aug 2026
   *  feedback set — no email/phone). An explicit empty list means "no rows",
   *  which skips the whole section. */
  personalDetails?: PersonalDetailField[];
  /** Section order and display labels. Sections with no content are skipped
   *  entirely (spec FR-023) — except consultantSummary, which renders its
   *  labels even when empty so consultants can complete it in Word. */
  sections: TemplateSection[];
  /** When true, achievements/bullets get visual priority over technical
   *  detail (Consumer template, TPL-C-003). Currently: bullets first, skills
   *  rendered as a compact run-in list. */
  emphasizeAchievements: boolean;
}

export interface TemplateInfo {
  id: string;
  version: number;
  name: string;
  description: string;
  sections: TemplateSection[];
  /** Full definition so the review screen can render a live in-browser
   *  preview of the formatted document while fields are edited. */
  definition: TemplateDefinition;
  /** Presigned asset links (null for seed templates whose art lives on the
   *  server's disk rather than in object storage). */
  logoUrl: string | null;
  watermarkUrl: string | null;
}

export type TemplateStatus = "draft" | "published";

/** One stored template version (admin/builder API). Drafts are editable;
 *  published rows are immutable — changes always create a new draft version. */
export interface TemplateRecord {
  rowId: string;
  templateId: string;
  version: number;
  status: TemplateStatus;
  definition: TemplateDefinition;
  /** Presigned view URLs for uploaded brand assets (builder preview). */
  logoUrl: string | null;
  watermarkUrl: string | null;
  publishedAt: string | null;
  updatedAt: string;
}

export const ALL_SECTION_KEYS: SectionKey[] = [
  "candidateReport",
  "personalDetails",
  "consultantSummary",
  "summary",
  "education",
  "experience",
  "coreCompetencies",
  "technicalSkills",
  "certifications",
  "publications",
  "careerInterests",
  "otherSections",
];

export const DEFAULT_SECTION_LABELS: Record<SectionKey, string> = {
  candidateReport: "Candidate Report (Strictly Private & Confidential)",
  personalDetails: "Personal Details",
  consultantSummary: "Consultant Summary",
  summary: "Summary",
  education: "Education",
  experience: "Professional Experience",
  coreCompetencies: "Core Competencies",
  technicalSkills: "Technical Skills",
  certifications: "Certifications",
  publications: "Papers & Publications",
  careerInterests: "Career Interests",
  otherSections: "Other Information",
};

// ---- generation --------------------------------------------------------------

export interface GenerateRequest {
  templateId: string;
  /** Include candidate email/phone in the output (off for client-facing). */
  includeContact: boolean;
  formats: Array<"docx" | "pdf">;
  /** Footer preset picked on the review screen; absent or null follows the
   *  template's default (see resolveFooterLines). */
  footerPreset?: string | null;
}

// ---- upload limits (spec FR-003: initial limits 20 MB / 30 pages) ------------

export const RESUME_MAX_BYTES = 20 * 1024 * 1024;
export const RESUME_MAX_PAGES = 30;

export const RESUME_ACCEPTED_TYPES: Record<string, string> = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "text/plain": "txt",
};

/** Fields below this confidence are highlighted for review (spec FR-034). */
export const LOW_CONFIDENCE_THRESHOLD = 0.75;

// ---- sample resume ----------------------------------------------------------

// Used by the template builder: the backend renders it into sample DOCX
// downloads and the frontend renders it into the live HTML preview, so both
// previews show identical content.
const sampleDate = (
  year: number | null,
  month: number | null,
  isPresent = false,
): ExtractedDate => ({ rawText: null, year, month, isPresent, confidence: 1 });

export const SAMPLE_RESUME: AgpResume = {
  candidate: {
    fullName: "Alice Tanaka",
    location: "Tokyo, Japan",
    nationality: "Japanese",
    email: "alice.tanaka@example.com",
    phone: "+81 90 1234 5678",
    referenceId: "112345",
    citizenship: "Japanese",
    dateOfBirth: "12 Sep 1993",
    annualSalary: "JPY 12,000,000",
    availability: "1 month notice",
    languages: [
      { language: "English", statedLevel: "Fluent", confidence: 1 },
      { language: "Japanese", statedLevel: "Native", confidence: 1 },
    ],
    summaryBullets: [
      {
        text: "Finance professional with 10 years across front-office trading support and risk.",
        kind: "bullet",
      },
      { text: "Track record of process automation and cross-regional projects.", kind: "bullet" },
    ],
    confidence: 1,
  },
  candidateReport: {
    client: "Pacific Securities K.K.",
    position: "Vice President, Trading Risk",
  },
  consultant: {
    preparedBy: "Kenji Sato",
    telephone: "080-1234-5678",
    email: "kenji.sato@ascentgp.com",
    date: "5 August 2026",
  },
  experience: [
    {
      employer: "Global Bank K.K.",
      employerLocation: "Tokyo",
      employerDescription: null,
      scale: null,
      start: sampleDate(2016, 4),
      end: sampleDate(null, null, true),
      roles: [
        {
          title: "Vice President, Equity Derivatives",
          start: sampleDate(2021, 10),
          end: sampleDate(null, null, true),
          bullets: [
            {
              text: "Led a team of four covering exotic equity products for APAC institutional clients.",
              kind: "bullet",
            },
            { text: "Designed and introduced an S&OP process.", kind: "sub" },
            { text: "Developed the supplier network and introduced new suppliers.", kind: "sub" },
            {
              text: "Automated daily risk reporting, cutting preparation time by 60% across the desk.",
              kind: "bullet",
            },
          ],
          confidence: 1,
        },
        {
          title: "Associate, Equity Derivatives",
          start: sampleDate(2016, 4),
          end: sampleDate(2021, 9),
          bullets: [
            {
              text: "Priced and executed structured products for institutional clients.",
              kind: "bullet",
            },
            {
              text: "Built pricing tools in Python used across the Tokyo and Hong Kong desks.",
              kind: "bullet",
            },
          ],
          confidence: 1,
        },
      ],
      confidence: 1,
    },
    {
      employer: "Consulting Partners",
      employerLocation: "Singapore",
      employerDescription:
        "Boutique advisory firm (120 staff) focused on strategy and due diligence for financial-sector clients.",
      scale: null,
      start: sampleDate(2014, null),
      end: sampleDate(2016, 3),
      roles: [
        {
          title: "Analyst",
          start: sampleDate(2014, null),
          end: sampleDate(2016, 3),
          bullets: [
            {
              text: "Supported due-diligence engagements for financial-sector clients.",
              kind: "bullet",
            },
          ],
          confidence: 1,
        },
      ],
      confidence: 1,
    },
  ],
  education: [
    {
      institution: "Example University",
      location: "Tokyo",
      qualification: "B.A.",
      field: "Economics",
      startYear: 2010,
      endYear: 2014,
      honours: "First-class honours",
      bullets: [],
      confidence: 1,
    },
  ],
  coreCompetencies: ["Equity Derivatives", "Structured Products", "Risk Management"],
  technicalSkills: ["Python", "Bloomberg", "Excel VBA"],
  certifications: [{ name: "CFA Level II", issuer: "CFA Institute", year: 2018, confidence: 1 }],
  publications: [],
  careerInterests: ["Front-office quant roles in Tokyo or Singapore"],
  otherSections: [],
  unmappedContent: [],
  extractionWarnings: [],
};

// ---- sample job description -------------------------------------------------

// Used by the JD template preview (builder and review screen fall back to it
// before real data exists). Content mirrors the supplied AGP sample.
export const SAMPLE_JD: AgpJobDescription = {
  title: "Investor Relations Director",
  company: "ORIX Corporation",
  location: "New York Office",
  details: [],
  sections: [
    {
      heading: "Position Overview:",
      lines: [
        {
          text: "ORIX Group seeks an Investor Relations Professional for its New York Office. The successful candidate will report to the Global Head of Investor Relations and have frequent interaction with the team in Tokyo, as well as with Senior Management.",
          kind: "para",
        },
      ],
    },
    {
      heading: "Responsibilities will include:",
      lines: [
        {
          text: "Assist in building and developing the equity story and aid in the creation of quarterly financial presentations.",
          kind: "bullet",
        },
        { text: "Help collect investor feedback and share it with internal stakeholders.", kind: "bullet" },
        { text: "Arrange and prepare Senior Management for participation in conferences.", kind: "bullet" },
      ],
    },
    {
      heading: "Qualifications and Experience:",
      lines: [
        {
          text: "7+ years of financial services experience in investor relations, equities, corporate strategy, or investment banking.",
          kind: "bullet",
        },
        { text: "Bilingual in English and Japanese. JLPT N1 or equivalent.", kind: "bullet" },
        { text: "Knowledge of financial markets and familiarity with the Japanese economy.", kind: "bullet" },
      ],
    },
  ],
  // No job-level contact: previews fall through to the template's fixed
  // jdContact (resolveJdContact), which is what real jobs do by default.
  contact: null,
  unmappedContent: [],
  extractionWarnings: [],
};

// ---- formatting helpers -----------------------------------------------------
// Shared by the DOCX renderer (backend) and the live HTML preview (frontend)
// so the two can never format dates, names or languages differently.

/** Grey used for section sub-headers when a template has no headingStyle at
 *  all — the AGP-preferred look. An explicit `highlight: null` beats this. */
export const DEFAULT_HEADING_HIGHLIGHT = "D9D9D9";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// prettier-ignore
const MONTHS_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Heading/name casing. "none" keeps the text exactly as typed; an undefined
 *  mode means "upper" (the historical default). */
export function applyCase(label: string, mode: "upper" | "title" | "none" | undefined): string {
  if (mode === "title") {
    return label.replace(/\S+/g, (w) => (w[0] ?? "").toUpperCase() + w.slice(1).toLowerCase());
  }
  if (mode === "none") return label;
  return label.toUpperCase();
}

/** Date formatting is precision-preserving (spec FR-016): year-only dates
 *  render as just the year in every format instead of inventing a month. */
export function formatDate(
  d: ExtractedDate | null,
  format: TemplateDefinition["dateFormat"],
): string {
  if (!d) return "";
  if (d.isPresent) return "Present";
  if (!d.year) return d.rawText ?? "";
  if (d.month == null) return String(d.year); // year-only stays year-only
  if (format === "yyyy.MM") return `${d.year}.${String(d.month).padStart(2, "0")}`;
  if (format === "MM/yyyy") return `${String(d.month).padStart(2, "0")}/${d.year}`;
  if (format === "yyyy年M月") return `${d.year}年${d.month}月`;
  if (format === "MMMM yyyy") return `${MONTHS_FULL[d.month - 1]} ${d.year}`;
  if (format === "MMM.'yy") {
    return `${MONTHS[d.month - 1]}.'${String(d.year % 100).padStart(2, "0")}`;
  }
  return `${MONTHS[d.month - 1]} ${d.year}`;
}

export function formatDateRange(
  start: ExtractedDate | null,
  end: ExtractedDate | null,
  format: TemplateDefinition["dateFormat"],
): string {
  const s = formatDate(start, format);
  const e = formatDate(end, format);
  if (!s && !e) return "";
  if (s && e) return `${s} – ${e}`;
  return s || e;
}

/** Education span in the template's date style: months (when the source
 *  stated them) render like experience dates, year-only entries stay plain
 *  years — "2010 – 2014", "Aug.'13 – Jul.'19", "Sep.'04 – 2006". */
export function formatEducationSpan(
  entry: EducationEntry,
  format: TemplateDefinition["dateFormat"],
): string {
  const date = (year: number | null, month: number | null | undefined): ExtractedDate | null =>
    year == null
      ? null
      : {
          rawText: null,
          year,
          month: month != null && month >= 1 && month <= 12 ? month : null,
          isPresent: false,
          confidence: 1,
        };
  return formatDateRange(
    date(entry.startYear, entry.startMonth),
    date(entry.endYear, entry.endMonth),
    format,
  );
}

/** One "Languages" line, e.g. "English – Fluent, Japanese – Native" (dash,
 *  the AGP-preferred style) or "English (Fluent)" (paren). Languages with no
 *  stated level render as just the language name in either style. */
export function formatLanguages(
  languages: Array<Pick<LanguageEntry, "language" | "statedLevel">>,
  format: "dash" | "paren" | undefined,
): string {
  return languages
    .map((l) => {
      if (!l.statedLevel) return l.language;
      return format === "paren"
        ? `${l.language} (${l.statedLevel})`
        : `${l.language} – ${l.statedLevel}`;
    })
    .join(", ");
}

/** The [label, value] rows Personal Details renders, resolved from the
 *  template's row config. Rows with no value are skipped; email/phone
 *  additionally need the per-job includeContact flag (client-facing copies
 *  omit contact details even when a template lists the rows). */
export function personalDetailRows(
  resume: AgpResume,
  tpl: TemplateDefinition,
  includeContact: boolean,
): Array<[string, string]> {
  return personalDetailEntries(resume, tpl, includeContact).map((e) => [e.label, e.value]);
}

/** Same rows with the candidate field each one came from, for the preview
 *  to write edits back. */
export function personalDetailEntries(
  resume: AgpResume,
  tpl: TemplateDefinition,
  includeContact: boolean,
): Array<{ key: PersonalDetailKey; label: string; value: string }> {
  const c = resume.candidate;
  const values: Record<PersonalDetailKey, string | null> = {
    referenceId: c.referenceId ?? null,
    fullName: c.fullName,
    location: c.location,
    nationality: c.nationality,
    citizenship: c.citizenship ?? null,
    languages: c.languages.length > 0 ? formatLanguages(c.languages, tpl.languageFormat) : null,
    dateOfBirth: c.dateOfBirth ?? null,
    annualSalary: c.annualSalary ?? null,
    availability: c.availability ?? null,
    email: includeContact ? c.email : null,
    phone: includeContact ? c.phone : null,
  };
  const fields = tpl.personalDetails ?? DEFAULT_PERSONAL_DETAILS;
  return fields.flatMap((f) => {
    const v = values[f.key];
    return v ? [{ key: f.key, label: f.label, value: v }] : [];
  });
}

/** The [label, value] rows of the Consultant Summary. Unlike every other
 *  section, empty values still produce a row — a blank line the consultant
 *  fills in after generation (AGP feedback: manual consultant input). */
export function consultantRows(resume: AgpResume): Array<[string, string]> {
  const con = resume.consultant;
  return [
    ["Prepared by:", con?.preparedBy ?? ""],
    ["Telephone:", con?.telephone ?? ""],
    ["Email:", con?.email ?? ""],
    ["Date:", con?.date ?? ""],
  ];
}

/** The Client: / Position: rows opening the Candidate Report. Same rule as
 *  the Consultant Summary: labels always render, empty values stay blank. */
export function candidateReportRows(resume: AgpResume): Array<[string, string]> {
  const r = resume.candidateReport;
  return [
    ["Client:", r?.client ?? ""],
    ["Position:", r?.position ?? ""],
  ];
}

export * from "./lineEdit.js";
